import {
  State,
  TransformsPlugin,
  RenderPlugin,
  TweenPlugin,
  minimalLight,
} from '@multiplekex/shallot';
import { OrbitPlugin, LinesPlugin, ArrowsPlugin, TextPlugin } from '@multiplekex/shallot/extras';
import {
  createStepPlugin,
  setupCanvas,
  setupScrollPassthrough,
  setupDemo,
  TokenIndex,
} from '@blog-components/sequence';
import { Text } from '@multiplekex/shallot/extras';
import { SpecdecPlugin, BorderPlugin } from '../src';

const GROUP_INPUT = 0;
const GROUP_OUTPUT = 1;
const GROUP_DRAFT_INPUT = 2;
const GROUP_DRAFT_OUTPUT = 3;
const GROUP_DRAFT_MARK = 4;

const STEPS = [
  { title: 'Input', description: 'We have a sequence of <span class="term-input">input tokens</span>. We want to predict the next six.' },
  { title: 'Target Model', description: 'We feed them to a large language model — the <span class="term-target-model">target model</span>.' },
  { title: 'First Prediction', description: 'The <span class="term-target-model">target model</span> predicts the next token: <span class="term-output">"bees"</span>.' },
  { title: 'Sequential Generation', description: 'We repeat this process, generating one token per pass. This is slow.' },
  { title: 'Draft Model', description: 'Now suppose we have a smaller, faster model — the <span class="term-draft-model">draft model</span>.' },
  { title: 'Draft Output', description: 'The <span class="term-draft-model">draft model</span> generates tokens quickly, but with lower quality.' },
  { title: 'Validation', description: 'Now, what if we could use our <span class="term-target-model">target model</span> to accept or reject these tokens?' },
  { title: 'Speculative Decoding', description: 'That\'s <b>speculative decoding</b>. Draft quickly, then validate all at once.' },
];

async function init(): Promise<void> {
  const canvas = document.getElementById('canvas') as HTMLCanvasElement;
  if (!canvas) return;

  setupCanvas(canvas);
  setupScrollPassthrough(canvas);
  window.addEventListener('resize', () => setupCanvas(canvas));

  const state = await State.new()
    .withCanvas(canvas)
    .withLoading(minimalLight(canvas))
    .withPlugins(
      TransformsPlugin,
      RenderPlugin,
      OrbitPlugin,
      LinesPlugin,
      ArrowsPlugin,
      TextPlugin,
      TweenPlugin,
      createStepPlugin(),
      SpecdecPlugin,
      BorderPlugin
    )
    .withScene('../scenes/overview.scene')
    .build();

  const inputTokens = ['The', 'scientist', 'discovered', 'that'];
  const outputTokens = ['bees', 'communicate', 'with', 'a', 'wiggle', 'dance'];
  const draftOutputTokens = ['bees', 'communicate', 'using', 'a', 'buzz', 'sound'];
  const validationMarks = ['✓', '✓', '✓', '✓', '✗', '✗'];

  for (const eid of state.query([TokenIndex, Text])) {
    const idx = TokenIndex.index[eid];
    const group = TokenIndex.group[eid];

    if (group === GROUP_INPUT && idx < inputTokens.length) {
      Text.content[eid] = inputTokens[idx];
    } else if (group === GROUP_OUTPUT && idx < outputTokens.length) {
      Text.content[eid] = outputTokens[idx];
    } else if (group === GROUP_DRAFT_INPUT && idx < inputTokens.length) {
      Text.content[eid] = inputTokens[idx];
    } else if (group === GROUP_DRAFT_OUTPUT && idx < draftOutputTokens.length) {
      Text.content[eid] = draftOutputTokens[idx];
    } else if (group === GROUP_DRAFT_MARK && idx < validationMarks.length) {
      Text.content[eid] = validationMarks[idx];
    }
  }

  setupDemo(canvas, state, STEPS);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
